import React from 'react';
import { AlertTriangle, Copy, Scale } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useLanguage } from '../../../contexts/LanguageContext';
import { itemPrice, volumeCbm } from '../pricing';

const PAYMENT_STATUSES = ['unpaid', 'paid', 'advance'];

const METHODS = (isFr) => [
  { value: 'cash', label: isFr ? 'Espèces' : 'Cash' },
  { value: 'virement', label: isFr ? 'Virement' : 'Bank transfer' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'carte', label: isFr ? 'Carte bancaire' : 'Card' },
];

const money = (n) => `${n.toFixed(2)} €`;

/** Règlement d'un colis : prix, état, avance, moyen. */
const ItemPayment = ({
  item, index, errors, variant, isFr, isMaritime, suggestion, onItem, onCopyToAll, showCopy,
}) => {
  const statusLabels = {
    unpaid: isFr ? 'Non payé' : 'Unpaid',
    paid: isFr ? 'Réglé' : 'Paid',
    advance: isFr ? 'Avance' : 'Advance',
  };
  const err = (field) => errors[`items.${index}.${field}`];

  return (
    <div className="rounded-lg border border-border p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          {isFr ? 'Colis' : 'Package'} {index + 1}
          {item.description ? <span className="font-normal text-muted-foreground"> · {item.description}</span> : null}
        </h3>
        <span className="text-xs text-muted-foreground">
          {isMaritime ? `${volumeCbm(item).toFixed(3)} CBM` : `${item.weight || 0} kg`}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {variant === 'partner' ? (
          <div className="space-y-2">
            <Label>{isFr ? 'Prix de ce colis (€)' : 'Price of this package (€)'} *</Label>
            <Input
              type="number" step="0.01" min="0" placeholder="0.00"
              value={item.price}
              onChange={(e) => onItem(index, 'price', e.target.value)}
              className={err('price') ? 'border-red-500' : ''}
            />
            {suggestion > 0 && (
              <button
                type="button"
                onClick={() => onItem(index, 'price', String(suggestion))}
                className="text-xs text-accent underline-offset-2 hover:underline text-left"
              >
                {isFr ? 'Tarif de la route : ' : 'Route rate: '}{money(suggestion)}
                {itemPrice(item) !== suggestion ? (isFr ? ' — appliquer' : ' — apply') : ''}
              </button>
            )}
            {err('price') && <p className="text-xs text-red-500">{err('price')}</p>}
          </div>
        ) : (
          <div className="space-y-2">
            <Label>{isFr ? 'Montant' : 'Amount'}</Label>
            <p className="flex h-10 items-center rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground">
              {isFr ? 'Calculé par le transitaire' : 'Set by the freight operator'}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>{isFr ? 'Moyen de paiement' : 'Payment method'} *</Label>
          <select
            value={item.payment_method}
            onChange={(e) => onItem(index, 'payment_method', e.target.value)}
            className={`w-full border rounded-md px-3 py-2 text-sm bg-background ${
              err('payment_method') ? 'border-red-500' : 'border-border'
            }`}
          >
            <option value="">{isFr ? 'Sélectionner' : 'Select'}</option>
            {METHODS(isFr).map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {err('payment_method') && <p className="text-xs text-red-500">{err('payment_method')}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{isFr ? 'État du règlement' : 'Payment state'} *</Label>
        <div className="flex flex-wrap gap-2">
          {PAYMENT_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => {
                onItem(index, 'payment_status', status);
                if (status !== 'advance') onItem(index, 'advance_amount', '');
              }}
              className={`flex-1 basis-[7rem] rounded-md border-2 px-3 py-2 text-sm transition-all ${
                item.payment_status === status
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-border text-muted-foreground hover:border-slate-300'
              }`}
            >
              {statusLabels[status]}
            </button>
          ))}
        </div>
        {err('payment_status') && <p className="text-xs text-red-500">{err('payment_status')}</p>}
      </div>

      {item.payment_status === 'advance' && (
        <div className="space-y-2">
          <Label>{isFr ? 'Montant avancé (€)' : 'Advance paid (€)'} *</Label>
          <Input
            type="number" step="0.01" min="0" placeholder="0.00"
            value={item.advance_amount}
            onChange={(e) => onItem(index, 'advance_amount', e.target.value)}
            className={err('advance_amount') ? 'border-red-500' : ''}
          />
          {err('advance_amount') && <p className="text-xs text-red-500">{err('advance_amount')}</p>}
        </div>
      )}

      {showCopy && (
        <Button type="button" variant="outline" size="sm" onClick={onCopyToAll}>
          <Copy className="h-3.5 w-3.5 mr-2" />
          {isFr ? 'Appliquer ce règlement à tous les colis' : 'Apply this payment to every package'}
        </Button>
      )}
    </div>
  );
};

/** Étape 4 — règlement colis par colis, puis récapitulatif de l'envoi. */
const PaymentStep = ({
  form, errors = {}, variant = 'partner', totals, onItem, onField, pricingInfo, companyName, hideCharter = false,
}) => {
  const { language } = useLanguage();
  const isFr = language !== 'en';
  const isMaritime = form.transport_mode === 'maritime';
  const multi = form.items.length > 1;

  const suggestionFor = (item, index) => {
    if (!pricingInfo?.found) return 0;
    return pricingInfo.suggest(item, index);
  };

  const copyPaymentToAll = (index) => {
    const source = form.items[index];
    form.items.forEach((_, i) => {
      if (i === index) return;
      onItem(i, 'payment_status', source.payment_status);
      onItem(i, 'payment_method', source.payment_method);
      onItem(i, 'advance_amount', source.payment_status === 'advance' ? source.advance_amount : '');
    });
  };

  const noPricing = variant === 'partner' && pricingInfo?.found === false;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
        <span className="text-muted-foreground">
          {isMaritime ? (isFr ? 'Volume total' : 'Total volume') : (isFr ? 'Poids total' : 'Total weight')}
        </span>
        <span className="flex items-center gap-2 font-semibold text-foreground">
          <Scale className="h-4 w-4 text-muted-foreground" />
          {isMaritime ? `${totals.volumeCbm.toFixed(4)} CBM` : `${totals.weight.toFixed(2)} kg`}
        </span>
      </div>

      {noPricing && (
        <div className="flex items-start gap-2 rounded border border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            {isFr
              ? 'Aucun tarif ne couvre ce trajet : les prix sont à saisir à la main, sinon les factures partiront à 0.'
              : 'No rate covers this route: prices must be entered by hand, otherwise invoices will be issued at 0.'}
          </span>
        </div>
      )}

      <div className="space-y-3">
        {form.items.map((item, index) => (
          <ItemPayment
            key={item.id}
            item={item}
            index={index}
            errors={errors}
            variant={variant}
            isFr={isFr}
            isMaritime={isMaritime}
            suggestion={suggestionFor(item, index)}
            onItem={onItem}
            showCopy={multi && index === 0}
            onCopyToAll={() => copyPaymentToAll(index)}
          />
        ))}
      </div>

      {/* Récapitulatif : ce que l'envoi coûte, ce qui est réglé, ce qui reste dû. */}
      {variant === 'partner' && (
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="text-muted-foreground">
              {totals.count} {isFr ? (totals.count > 1 ? 'colis' : 'colis') : (totals.count > 1 ? 'packages' : 'package')}
            </span>
            <span className="text-lg font-semibold text-foreground">{money(totals.price)}</span>
          </div>
          <div className="mt-3 space-y-1.5 border-t border-border pt-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isFr ? 'Déjà réglé' : 'Already paid'}</span>
              <span className="font-medium text-green-700">{money(totals.paid)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{isFr ? 'Reste dû' : 'Still due'}</span>
              <span className={`font-semibold ${totals.due > 0 ? 'text-foreground' : 'text-green-700'}`}>
                {money(totals.due)}
              </span>
            </div>
          </div>
        </div>
      )}

      {!hideCharter && (
        <div className="space-y-3 border-t pt-4">
          <p className="text-sm font-semibold text-foreground">
            {isFr ? 'Charte expéditeur' : 'Sender charter'}
          </p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.charter.inspectionConsent}
              onChange={(e) => onField('charter.inspectionConsent', e.target.checked)}
              className="mt-1 shrink-0"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {isFr
                ? `J'autorise ${companyName || 'la société'} à ouvrir mon colis en cas de besoin de vérifier la cohérence entre la déclaration et le contenu. Toute fausse déclaration pourrait entraîner un redressement.`
                : `I authorize ${companyName || 'the company'} to open my package if necessary to verify the consistency between the declaration and the contents. Any false declaration may result in an adjustment.`}
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.charter.termsAccepted}
              onChange={(e) => onField('charter.termsAccepted', e.target.checked)}
              className="mt-1 shrink-0"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {isFr
                ? 'J\'accepte avoir pris connaissance des conditions générales qui régissent les opérations de transport.'
                : 'I accept having read and understood the general terms and conditions governing transport operations.'}
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.charter.noProhibitedItems}
              onChange={(e) => onField('charter.noProhibitedItems', e.target.checked)}
              className="mt-1 shrink-0"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {isFr
                ? `Je reconnais n'avoir introduit aucun produit non réglementé, interdit ou volé auprès de ${companyName || 'la société'}.`
                : `I certify that I have not included any prohibited, unregulated, or stolen products with ${companyName || 'the company'}.`}
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default PaymentStep;
